import { useQuery } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient, downloadFile } from "./client";
import type { AgingReport } from "./payments";
import type { LegalCaseStatus } from "./legal";
import type { ProcedureStatus } from "./procedures";

export interface RevenueByServiceRow {
  description: string;
  totalRevenue: number;
  invoiceCount: number;
}

export function useRevenueByService(filters: { dateFrom?: string; dateTo?: string } = {}) {
  return useQuery({
    queryKey: ["reports", "revenue-by-service", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<RevenueByServiceRow[]>>("/reports/revenue-by-service", { params: filters });
      return data.data;
    },
  });
}

export interface ProductivityRow {
  userId: string;
  userName: string;
  assigned: number;
  completed: number;
  completionRate: number;
}

export function useProductivity() {
  return useQuery({
    queryKey: ["reports", "productivity"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ProductivityRow[]>>("/reports/productivity");
      return data.data;
    },
  });
}

export interface UpcomingTaxRow {
  id: string;
  clientId: string;
  clientName: string;
  taxType: string;
  periodLabel: string;
  dueDate: string;
  status: string;
  amount: string | null;
  daysUntilDue: number;
}

export function useUpcomingTaxes(daysAhead = 30) {
  return useQuery({
    queryKey: ["reports", "upcoming-taxes", daysAhead],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<UpcomingTaxRow[]>>("/reports/upcoming-taxes", { params: { daysAhead } });
      return data.data;
    },
  });
}

export type DeadlineType = "TRAMITE" | "IMPUESTO" | "JURIDICO" | "FACTURA" | "DOCUMENTO";

export const DEADLINE_TYPE_LABEL: Record<DeadlineType, string> = {
  TRAMITE: "Trámite",
  IMPUESTO: "Impuesto",
  JURIDICO: "Jurídico",
  FACTURA: "Factura",
  DOCUMENTO: "Documento",
};

export interface UpcomingDeadlineRow {
  id: string;
  type: DeadlineType;
  label: string;
  clientId: string;
  clientName: string;
  dueDate: string;
  daysUntilDue: number;
}

/** "Alertas de vencimientos": une trámites, impuestos, próximas actuaciones jurídicas, facturas por cobrar y documentos del cliente por vencer en una sola lista ordenada por urgencia. */
export function useUpcomingDeadlines(daysAhead = 30) {
  return useQuery({
    queryKey: ["reports", "upcoming-deadlines", daysAhead],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<UpcomingDeadlineRow[]>>("/reports/upcoming-deadlines", { params: { daysAhead } });
      return data.data;
    },
  });
}

export interface LegalProceduresKpis {
  legal: {
    total: number;
    byStatus: Record<LegalCaseStatus, number>;
    upcomingHearings7d: number;
    claimAmountActive: number;
  };
  procedures: {
    total: number;
    byStatus: Record<ProcedureStatus, number>;
    overdue: number;
    dueSoon7d: number;
    costActive: number;
  };
}

/** "Reportes e indicadores": tarjetas de KPI de carga de trabajo jurídica y de trámites. */
export function useLegalProceduresKpis() {
  return useQuery({
    queryKey: ["reports", "legal-procedures-kpis"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<LegalProceduresKpis>>("/reports/legal-procedures-kpis");
      return data.data;
    },
  });
}

export interface StoreReportDayRow {
  day: string;
  total: number;
  count: number;
  unitsSold: number;
  customersServed: number;
}

export interface StoreReportPaymentRow {
  paymentMethod: string;
  total: number;
  count: number;
}

export interface StoreReportProductRow {
  productId: string;
  name: string;
  sku: string;
  quantitySold: number;
  revenue: number;
  costEstimate: number;
  profitEstimate: number;
}

export interface StoreReportCategoryRow {
  categoryId: string | null;
  categoryName: string;
  revenue: number;
  quantitySold: number;
}

export interface StoreSalesReport {
  dateFrom: string;
  dateTo: string;
  kpis: {
    salesCount: number;
    subtotal: number;
    taxTotal: number;
    total: number;
    costEstimate: number;
    profitEstimate: number;
    marginPercent: number;
    averageTicket: number;
    unitsSold: number;
    customersServed: number;
  };
  byDay: StoreReportDayRow[];
  byPaymentMethod: StoreReportPaymentRow[];
  topProductsByRevenue: StoreReportProductRow[];
  topProductsByProfit: StoreReportProductRow[];
  topProductsByQuantity: StoreReportProductRow[];
  byCategory: StoreReportCategoryRow[];
  byBranch: StoreReportBranchRow[];
}

export interface StoreReportBranchRow {
  branchId: string;
  branchName: string;
  total: number;
  count: number;
  averageTicket: number;
}

/** Reporte de ventas/rentabilidad de la tienda — la rentabilidad es una estimación con el costo ACTUAL del producto (no se guarda costo histórico por venta). `branchId` filtra a una sola sucursal; `byBranch` siempre trae el comparativo entre todas. */
export function useStoreSalesReport(filters: { dateFrom?: string; dateTo?: string; branchId?: string; enabled?: boolean } = {}) {
  const { enabled = true, ...params } = filters;
  return useQuery({
    queryKey: ["reports", "store-sales", params],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<StoreSalesReport>>("/reports/store-sales", { params });
      return data.data;
    },
    enabled,
  });
}

export interface InventoryValueRow {
  productId: string;
  sku: string;
  name: string;
  stockQty: number;
  costPrice: number;
  value: number;
  minStock: number;
  isLowStock: boolean;
}

export interface InventoryValueReport {
  totalValue: number;
  totalUnits: number;
  lowStockCount: number;
  rows: InventoryValueRow[];
}

/** Valor total de inventario (costo x cantidad) y detección de stock bajo mínimo, con el stock AGREGADO de todas las sucursales. */
export function useInventoryValueReport() {
  return useQuery({
    queryKey: ["reports", "store-inventory-value"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<InventoryValueReport>>("/reports/store-inventory-value");
      return data.data;
    },
  });
}

export interface CommissionReportRow {
  userId: string;
  userName: string;
  salesCount: number;
  salesTotal: number;
  commissionRate: number;
  commissionAmount: number;
}

export interface CommissionReport {
  dateFrom: string;
  dateTo: string;
  totalCommission: number;
  rows: CommissionReportRow[];
}

/** Comisión por vendedor (Sale.createdByUserId) sobre sus ventas COMPLETADA del periodo — % configurable por usuario en Usuarios. */
export function useCommissionsReport(filters: { dateFrom?: string; dateTo?: string } = {}) {
  return useQuery({
    queryKey: ["reports", "store-commissions", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<CommissionReport>>("/reports/store-commissions", { params: filters });
      return data.data;
    },
  });
}

export interface StockoutForecastItem {
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  currentStock: number;
  avgDailyDemand: number;
  daysRemaining: number;
  estimatedStockoutDate: string;
  suggestedOrderQty: number;
}

/** Predicción de quiebre de stock: productos que, al ritmo de venta promedio de los últimos 30 días, se agotarían dentro de `daysAhead` días. */
export function useStockoutForecast(daysAhead = 14) {
  return useQuery({
    queryKey: ["reports", "store-stockout-forecast", daysAhead],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<StockoutForecastItem[]>>("/reports/store-stockout-forecast", { params: { daysAhead } });
      return data.data;
    },
  });
}

export function useCarteraSummary() {
  return useQuery({
    queryKey: ["reports", "cartera-summary"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AgingReport>>("/reports/cartera-summary");
      return data.data;
    },
  });
}

export interface ShrinkageRow {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  branchId: string;
  branchName: string;
  quantity: number;
  reason: string;
  notes: string | null;
  costEstimate: number;
  createdAt: string;
  createdByName: string | null;
}

export interface ShrinkageByReasonRow {
  reason: string;
  quantity: number;
  costEstimate: number;
  count: number;
}

export interface ShrinkageByProductRow {
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  costEstimate: number;
  count: number;
}

export interface ShrinkageReport {
  dateFrom: string | null;
  dateTo: string | null;
  totalQuantity: number;
  totalCostEstimate: number;
  movementsCount: number;
  byReason: ShrinkageByReasonRow[];
  byProduct: ShrinkageByProductRow[];
  rows: ShrinkageRow[];
}

/** Reporte de Mermas: agrega la bitácora de movimientos MERMA por motivo y por producto — ver store-report.service.ts::getShrinkageReport. */
export function useShrinkageReport(filters: { dateFrom?: string; dateTo?: string; branchId?: string } = {}) {
  return useQuery({
    queryKey: ["reports", "store-shrinkage", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ShrinkageReport>>("/reports/store-shrinkage", { params: filters });
      return data.data;
    },
  });
}

export interface BelowCostSaleRow {
  saleLineId: string;
  saleId: string;
  saleNumber: number;
  saleDate: string;
  branchId: string;
  branchName: string;
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  unitCost: number;
  effectiveUnitPrice: number;
  lossPerUnit: number;
  lossTotal: number;
  createdByName: string | null;
}

export interface BelowCostSalesReport {
  dateFrom: string;
  dateTo: string;
  totalLossEstimate: number;
  lineCount: number;
  rows: BelowCostSaleRow[];
}

/** Ventas cuyo precio efectivo quedó por debajo del costo del producto — dato histórico real (unit_cost congelado en cada línea al momento de la venta), no una estimación con el costo actual. Ver store-report.service.ts::getBelowCostSalesReport y pos.service.ts::createSale (acknowledgeBelowCost). */
export function useBelowCostSalesReport(filters: { dateFrom?: string; dateTo?: string; branchId?: string } = {}) {
  return useQuery({
    queryKey: ["reports", "store-below-cost-sales", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<BelowCostSalesReport>>("/reports/store-below-cost-sales", { params: filters });
      return data.data;
    },
  });
}

export interface TransferSuggestionItem {
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  minStock: number;
  fromBranchId: string;
  fromBranchName: string;
  fromBranchStock: number;
  toBranchId: string;
  toBranchName: string;
  toBranchStock: number;
  suggestedQty: number;
}

/** Sugerencias de traslado: productos con excedente (por encima de su stock mínimo) en una sucursal y faltante (por debajo) en otra — ver store-report.service.ts::getTransferSuggestions. Solo informativo; crear el traslado real sigue usando useCreateStockTransfer de "./inventory". */
export function useTransferSuggestions() {
  return useQuery({
    queryKey: ["reports", "store-transfer-suggestions"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<TransferSuggestionItem[]>>("/reports/store-transfer-suggestions");
      return data.data;
    },
  });
}

export interface SalesGoalProgressRow {
  userId: string;
  userName: string;
  monthlyGoal: number;
  salesTotal: number;
  progressPercent: number;
  remaining: number;
  projectedTotal: number;
  onTrack: boolean;
}

export interface SalesGoalsProgressReport {
  year: number;
  month: number;
  monthLabel: string;
  isCurrentMonth: boolean;
  daysElapsed: number;
  daysInMonth: number;
  totalGoal: number;
  totalSales: number;
  totalProgressPercent: number;
  rows: SalesGoalProgressRow[];
}

/** Progreso de metas de venta mensuales por vendedor — meta configurable por usuario en Usuarios (`monthlyGoal`). Ver store-report.service.ts::getSalesGoalsProgress. */
export function useSalesGoalsProgress(filters: { year?: number; month?: number } = {}) {
  return useQuery({
    queryKey: ["reports", "store-sales-goals-progress", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<SalesGoalsProgressReport>>("/reports/store-sales-goals-progress", { params: filters });
      return data.data;
    },
  });
}

/** @deprecated usa `downloadFile` de "./client" directamente (se mantiene este alias para no tocar cada call-site existente). */
export const downloadReportCsv = downloadFile;
